import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, ChevronLeft } from "lucide-react";
import {
  useAgentWorkflowActionsStore,
  ActionDefinition,
} from "@/store/agentWorkflowActionsStore";
import { useAgentWorkflowAppsStore, App } from "@/store/agentWorkflowAppsStore";

interface SelectActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectAction: (action: ActionDefinition) => void;
  filterTypes?: Array<"ACTION" | "CONDITION" | "AGENT" | "MESSAGE">;
}

export function SelectActionModal({
  open,
  onOpenChange,
  onSelectAction,
  filterTypes,
}: SelectActionModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const actions = useAgentWorkflowActionsStore((state) => state.actions);
  const actionState = useAgentWorkflowActionsStore(
    (state) => state.actionState,
  );
  const fetchAppActions = useAgentWorkflowActionsStore(
    (state) => state.fetchAppActions,
  );
  const getActionApps = useAgentWorkflowAppsStore(
    (state) => state.getActionApps,
  );
  const appState = useAgentWorkflowAppsStore((state) => state.appState);
  const fetchActionApps = useAgentWorkflowAppsStore(
    (state) => state.fetchActionApps,
  );
  const actionAppIds = useAgentWorkflowAppsStore((state) => state.actionAppIds);

  useEffect(() => {
    if (open && actionAppIds.length === 0 && !appState.isLoadingGet) {
      fetchActionApps();
    }
  }, [open, actionAppIds.length, appState.isLoadingGet, fetchActionApps]);

  useEffect(() => {
    if (selectedApp) {
      fetchAppActions(selectedApp.id);
    }
  }, [selectedApp, fetchAppActions]);

  const appsArray = getActionApps();
  const filteredApps = appsArray.filter((app) => {
    return (
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const actionsArray = selectedApp
    ? selectedApp.actionIds
        .map((id) => actions.get(id))
        .filter((action): action is ActionDefinition => action !== undefined)
    : [];
  const filteredActions = actionsArray.filter((action) => {
    if (filterTypes && !filterTypes.includes(action.type)) {
      return false;
    }
    return (
      action.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      action.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleSelectAction = (action: ActionDefinition) => {
    onSelectAction(action);
    onOpenChange(false);
    setSearchQuery("");
    setSelectedApp(null);
  };

  const handleSelectApp = (app: App) => {
    setSelectedApp(app);
    setSearchQuery("");
  };

  const handleBack = () => {
    setSelectedApp(null);
    setSearchQuery("");
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedApp(null);
      setSearchQuery("");
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-2xl mx-auto">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">
            {selectedApp ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="h-9 w-9 sm:h-auto sm:w-auto p-1"
                >
                  <ChevronLeft className="h-5 w-5 sm:h-4 sm:w-4" />
                </Button>
                <span className="truncate">{selectedApp.name}</span>
              </div>
            ) : (
              "Add Action"
            )}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {selectedApp
              ? "Select an action from this app"
              : "Select an app to browse its actions"}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-10 sm:h-9"
          />
        </div>

        <ScrollArea className="h-[50vh] sm:h-[400px] pr-4">
          {!selectedApp ? (
            appState.isLoadingGet ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-muted-foreground">Loading apps...</div>
              </div>
            ) : appState.getError ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-destructive">{appState.getError}</div>
              </div>
            ) : filteredApps.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-muted-foreground">
                  {searchQuery ? "No apps found" : "No apps available"}
                </div>
              </div>
            ) : (
              <div className="grid gap-2">
                {filteredApps.map((app) => (
                  <Button
                    key={app.id}
                    variant="outline"
                    className="h-auto justify-start p-3 sm:p-4 text-left active:bg-muted/70 w-full overflow-hidden"
                    onClick={() => handleSelectApp(app)}
                  >
                    <div className="flex items-start gap-2 sm:gap-3 w-full min-w-0">
                      {app.imageUrl && (
                        <img
                          src={app.imageUrl}
                          alt={app.name}
                          className="h-8 w-8 rounded object-contain flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="font-medium text-sm sm:text-base truncate">
                          {app.name}
                        </div>
                        <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                          {app.description}
                        </div>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            )
          ) : actionState.isLoadingGet ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading actions...</div>
            </div>
          ) : actionState.getError ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-destructive">{actionState.getError}</div>
            </div>
          ) : filteredActions.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">
                {searchQuery ? "No actions found" : "No actions available"}
              </div>
            </div>
          ) : (
            <div className="grid gap-2">
              {filteredActions.map((action) => (
                <Button
                  key={action.id}
                  variant="outline"
                  className="h-auto justify-start p-3 sm:p-4 text-left active:bg-muted/70 w-full overflow-hidden"
                  onClick={() => handleSelectAction(action)}
                >
                  <div className="flex items-start gap-2 sm:gap-3 w-full min-w-0">
                    {action.imageUrl && (
                      <img
                        src={action.imageUrl}
                        alt={action.name}
                        className="h-8 w-8 rounded object-contain flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="font-medium text-sm sm:text-base truncate">
                        {action.name}
                      </div>
                      <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                        {action.description}
                      </div>
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
